import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import CheckmarkOutline16 from '@carbon/icons-react/es/checkmark--outline/16';
import View16 from '@carbon/icons-react/es/view/16';
import ViewOff16 from '@carbon/icons-react/es/view--off/16';
import Warning16 from '@carbon/icons-react/es/warning/16';
import { OktaContext } from '@okta/okta-react';
import {
  Link,
  useLocationPathname,
  useLocationSearch,
  useNavigate,
} from '@rocketlane/rocket-router';
import { useMutation, useQuery } from '@tanstack/react-query';
// eslint-disable-next-line no-restricted-imports
import { Col, Image, Row, Space, Form as AntdForm } from 'antd';
import { orderBy } from 'lodash';
import React, {
  FC,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  AccountAPI,
  AuthenticationServiceProvider,
  IdpType,
} from 'api/account';
import { PermissionSetType, Role as RoleT, User, UserType } from 'api/models';
import { UserAPI } from 'api/user';
import {
  Container,
  Heading,
  LinkText,
  RowHelp,
  UserInfo,
} from 'components/atoms/form-elements';
import { Layout } from 'components/atoms/layout';
import { PageLoading } from 'components/atoms/loading';
import { selectPrefix } from 'components/atoms/select';
import { ReactComponent as Google } from 'components/atoms/vendor-loign/google.svg';
import { Button } from 'components/facade/button';
import { Input } from 'components/facade/input';
import { Select } from 'components/facade/select';
import { toastService } from 'components/molecules/toast';
import { useCognitoAuth } from 'components/organisms/login/use-cognito-auth';
import { useIsSamlMandatory, getEnabledIdp } from 'components/organisms/saml';
import { TokenErrorStatus } from 'components/organisms/token-error/constant';
import { HelperText } from 'components/styles';
import { RoutePaths } from 'constant';
import { RolesApi } from 'data/roles';
import { useCustomAuth } from 'hooks/use-custom-auth';
import { useQueryParams } from 'hooks/use-query-params';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useAccountMetaQuery } from 'queries';
import { logger } from 'services';
import { getPasswordFormErrorMessage } from 'utils/error';
import { logout } from 'utils/logout';
import { redirectToOAuth } from 'utils/oauth-redirect';
import { getTokenMeta, TokenLinkType } from 'utils/token';
import * as styles from './input.module.css';
import logo from './rl_logo_colored.svg';

const TokenError = React.lazy(() =>
  import('components/organisms/token-error/token-error').then((d) => ({
    default: d.TokenError,
  })),
);

enum PasswordsFields {
  New = 'newPassword',
  Confirm = 'confirmPassword',
}

enum FormFields {
  FName = 'firstName',
  LName = 'lastName',
  Role = 'role',
}

const StyledSpace = styled(Space)`
  width: 33.5rem;
`;

const Form = styled(AntdForm)`
  .ant-form-item.ant-form-item-has-error {
    .${selectPrefix}__control {
      outline-color: var(--error-color) !important;
    }
  }
`;
const FormItem = styled(AntdForm.Item)`
  padding-bottom: var(--spacing-05);
  &:not(.ant-form-item-with-help) {
    margin-bottom: var(--spacing-05);
  }

  .ant-form-item-explain,
  .ant-form-item-extra {
    ${HelperText};

    &.ant-form-item-explain-error {
      color: var(--error-color);
    }
  }
`;

const StyledRowHelp = styled(RowHelp)`
  flex: 0 0 100%;
  margin-bottom: var(--spacing-05);
`;

const DividerWrapper = styled.span`
  display: flex;
  gap: var(--spacing-03);
  align-items: center;
  padding-bottom: var(--spacing-05);
  span {
    width: 35ch;
    background-color: var(--black-100);
    opacity: 0.1;
    height: 1px;
  }
`;

const StyledWarningIcon = styled(Warning16)`
  color: var(--red-60);
`;

const StyledCheckmarkIcon = styled(CheckmarkOutline16)`
  color: var(--green-60);
`;

const StyledUserInfo = styled(UserInfo)`
  margin: var(--spacing-03) 0 var(--spacing-04) 0;
`;

export const SetupProfilePage: FC<React.PropsWithChildren<unknown>> = () => {
  const oktaContext = useContext(OktaContext);
  const { isAuthenticated: customIsAuthenticated } = useCustomAuth();
  const authState = oktaContext?.authState ?? {
    isAuthenticated: customIsAuthenticated,
  };
  const oktaAuth = oktaContext?.oktaAuth;

  const isAuthenticated = authState?.isAuthenticated;

  const { token, tokenManager } = oktaAuth ?? {};
  const pathname = useLocationPathname();
  const search = useLocationSearch();
  const replace = useNavigate();
  const redirect_uri = pathname + search;
  const [continueWithPassword, setContinueWithPassword] = useState(false);
  const [passwordToggle, setPasswordToggle] = useState({
    type: 'password',
    icon: ViewOff16,
  });
  const [warningVisibility, setWarningVisibility] = useState(false);
  const [successVisbility, setSuccessVisibility] = useState(false);
  const { data: accountMeta } = useAccountMetaQuery();
  const isCognitoEnabled =
    accountMeta?.okta?.authenticationServiceProvider ===
    AuthenticationServiceProvider.COGNITO;

  const params = useQueryParams();
  const urlToken = params.get('token') || '';
  const { data, isLoading } = useQuery(
    ['get-details-from-token', urlToken],
    () => UserAPI.getDetailsFromToken(urlToken),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: !isAuthenticated,
    },
  );
  const isPartnerUser = data?.sessionPayload.user.userType === UserType.Partner;
  const isPartnerAdmin =
    isPartnerUser &&
    data.sessionPayload.permissionSet.permissionSetType ===
      PermissionSetType.PartnerAdmin;
  // TODO: when we add sso for partner, make sure role field is filled up before proceeding to sso
  const isSamlEnabled = useMemo(
    () => !!(accountMeta?.okta.idps ?? []).filter((idp) => idp.enabled).length,
    [accountMeta?.okta.idps],
  );
  const isSSoEnabled = isPartnerUser ? false : isSamlEnabled;
  const isSamlMandate = useIsSamlMandatory();

  const { data: roles, isLoading: isRoleLoading } = useQuery(
    ['roles_from_token', urlToken],
    () => RolesApi.findAllWithToken(urlToken),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: !isAuthenticated,
    },
  );
  const roleOptions = useMemo(() => {
    const filteredRoles =
      (data?.sessionPayload.user.userType === UserType.Partner
        ? roles?.filter((role) => role.usedByPartners === true)
        : roles) ?? [];

    return orderBy(
      filteredRoles.map(({ roleName, roleId }) => ({
        label: roleName,
        value: roleId,
      })),
      [({ label }) => label.toLowerCase()],
      ['asc'],
    );
  }, [data?.sessionPayload.user.userType, roles]);
  const initialRole = useMemo(() => {
    const roleId = data?.sessionPayload.user.role?.roleId;

    return roleOptions.find((option) => option.value === roleId);
  }, [data?.sessionPayload.user.role?.roleId, roleOptions]);
  /**
   * if the user is authenticated, we will logout the old token
   * and then process the new token.
   * Since the earlier implementation followed redirect flow, it wasnt an issue.
   * In the latest implementation since we are preventing redirects to prevent tab refresh
   * to cut down unnecessary API calls
   * we maintain a state to identify if it is new token or not
   * to prevent logout of new token once authenticated.
   */
  const [isNewToken, setIsNewToken] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      if (!isNewToken) {
        logout(redirect_uri);
      } else {
        replace('/', { replace: true });
      }
    }
  }, [isAuthenticated, isNewToken, redirect_uri, replace]);

  useEffect(() => {
    if (isSSoEnabled) {
      return;
    }
    setContinueWithPassword(true);
  }, [isSSoEnabled]);

  const updateProfile = useCallback(
    (values: any) => {
      setIsNewToken(true);

      return UserAPI.setupProfileWithToken({
        firstName: values.firstName || '',
        lastName: values.lastName || '',
        newPassword: values.newPassword || '',
        confirmPassword: values.confirmPassword || '',
        token: urlToken || '',
      })
        .then((resp) => {
          if (isCognitoEnabled) {
            window.location.href = `${window.location.origin}/login?token=${urlToken}`;
          } else if (accountMeta?.okta?.cookiesEnabled) {
            if (accountMeta?.okta?.clientId) {
              redirectToOAuth(accountMeta.okta.clientId);
            }
          } else if (token) {
            return token
              .getWithoutPrompt({
                sessionToken: resp.sessionToken,
              })
              .then((res) => {
                tokenManager?.setTokens(res.tokens);
              })
              .catch((error) => {
                logger.error('Token setup failed', { error });

                return Promise.reject(error);
              });
          }

          return Promise.resolve();
        })
        .catch((error) => Promise.reject(error));
    },
    [isCognitoEnabled, token, tokenManager, urlToken, accountMeta],
  );

  const updateUser = useCallback(
    ({ firstName, lastName, roleId }: any) => {
      if (!data) {
        return;
      }
      const payload: AtLeast<User, 'userId'> = {
        userId: data.sessionPayload.user.userId,
      };

      if (firstName) {
        payload.firstName = firstName;
      }
      if (lastName) {
        payload.lastName = lastName;
      }

      if (roleId) {
        payload.role = { roleId } as RoleT;
      }

      UserAPI.save(payload, urlToken);
    },
    [data, urlToken],
  );

  const { mutateAsync: setupProfile, isLoading: isSetupProfilePending } =
    useMutation(updateProfile);

  const passwordMatchText = useTranslation('form.validations.password_match');
  const pwdPlaceholder = useTranslation('password.placeholder');
  const translate = useTranslate();
  const { isExpired, decoded } = getTokenMeta(urlToken);
  const idp = getEnabledIdp(accountMeta?.okta);

  const onFinish = (values: any) => {
    setSuccessVisibility(false);
    setupProfile(values).catch((error: any) => {
      const { primary, secondary } = getPasswordFormErrorMessage(
        error,
        translate('password.validation.unified'),
        translate('rte.error'),
      );

      toastService({
        title: translate('error.generic.title'),
        message: primary,
      }).error();

      if (secondary != null) {
        toastService({
          title: translate('error.generic.title'),
          message: secondary,
        }).error();
      }
    });
  };

  const { samlFlowHandler, googleSSOHandler } = useCognitoAuth({
    data: accountMeta,
  });

  if (!decoded) {
    return (
      <Suspense fallback={<PageLoading />}>
        <TokenError
          status={TokenErrorStatus.Invalid}
          linkType={TokenLinkType.AccountInvitation}
        />
      </Suspense>
    );
  }

  if (decoded && isExpired) {
    return (
      <Suspense fallback={<PageLoading />}>
        <TokenError
          status={TokenErrorStatus.Expired}
          email={decoded.emailId}
          resendEmail={() =>
            AccountAPI.memberActivateResend({ email: decoded.emailId })
          }
          linkType={decoded.linkType}
        />
      </Suspense>
    );
  }

  return isLoading || isAuthenticated ? (
    <PageLoading />
  ) : (
    <Layout>
      <Container>
        <StyledSpace direction="vertical" size={16}>
          <Image width={186} src={logo} preview={false} />
          <Heading>
            <FormattedMessage id="setup_profile.title" />
          </Heading>
          <Form layout="vertical" name="set-profile" onFinish={onFinish}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={FormFields.FName}
                  label={<FormattedMessage id="first_name" />}
                  initialValue={data?.sessionPayload?.user?.firstName}
                >
                  <Input
                    onBlur={(e) => updateUser({ firstName: e.target.value })}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={FormFields.LName}
                  label={<FormattedMessage id="last_name" />}
                  initialValue={data?.sessionPayload?.user?.lastName}
                >
                  <Input
                    onBlur={(e) => updateUser({ lastName: e.target.value })}
                  />
                </Form.Item>
              </Col>
              {data?.sessionPayload?.permissionSet ? (
                <Col span={12}>
                  <StyledRowHelp>
                    {isPartnerAdmin ? (
                      <FormattedMessage
                        id="setup_profile.partner_user_access_info"
                        values={{
                          permission: (
                            <strong>
                              {
                                data?.sessionPayload?.permissionSet
                                  ?.permissionSetName
                              }
                            </strong>
                          ),
                        }}
                      />
                    ) : (
                      <FormattedMessage
                        id="setup_profile.user_access_info"
                        values={{
                          role: (
                            <strong>
                              {data?.sessionPayload?.user?.role?.roleName}
                            </strong>
                          ),
                          permission: (
                            <strong>
                              {
                                data?.sessionPayload?.permissionSet
                                  ?.permissionSetName
                              }
                            </strong>
                          ),
                        }}
                      />
                    )}
                  </StyledRowHelp>
                </Col>
              ) : null}
            </Row>
            {isPartnerAdmin ? (
              <Row gutter={16}>
                <Col span={24}>
                  <FormItem
                    name={FormFields.Role}
                    label={<FormattedMessage id="role" />}
                    initialValue={initialRole}
                    rules={[
                      {
                        message: <FormattedMessage id="input.required" />,
                        required: isPartnerAdmin,
                      },
                    ]}
                  >
                    <Select
                      autoFocus
                      options={roleOptions}
                      isLoading={isRoleLoading}
                      onChange={(next) => updateUser({ roleId: next.value })}
                    />
                  </FormItem>
                </Col>
              </Row>
            ) : null}
            {isSSoEnabled && (
              <div>
                {idp && (
                  <Button
                    key={idp.idpId}
                    icon={idp.idpType === IdpType.Google && <Google />}
                    onClick={() => {
                      if (isCognitoEnabled) {
                        if (idp.idpType === IdpType.Google) {
                          googleSSOHandler();
                        } else {
                          samlFlowHandler();
                        }

                        return;
                      }
                      oktaAuth?.signInWithRedirect({
                        idp: idp.idpId,
                      });
                    }}
                  >
                    {idp.idpType === IdpType.Google
                      ? translate('google.sign-up')
                      : translate('setup_profile.sso.button')}
                  </Button>
                )}

                <StyledUserInfo>
                  <FormattedMessage
                    id={
                      isSamlMandate
                        ? 'sign_in.sso.mandate.hint'
                        : 'seup_profile.sso.info'
                    }
                  />
                </StyledUserInfo>
                {!isSamlMandate ? (
                  <DividerWrapper>
                    <FormattedMessage id="or" />
                    <span />
                  </DividerWrapper>
                ) : null}
              </div>
            )}
            {isSamlMandate && !isPartnerUser ? null : !continueWithPassword ? (
              <Button
                type="tertiary"
                onClick={() => setContinueWithPassword(true)}
              >
                <FormattedMessage id="setup_profile.password.button" />
              </Button>
            ) : (
              <>
                <FormItem
                  name={PasswordsFields.New}
                  rules={[
                    {
                      message: (
                        <FormattedMessage id="form.validations.new_password" />
                      ),
                    },
                    {
                      min: 11,
                      message: <FormattedMessage id="password.help" />,
                      validateTrigger: 'onBlur',
                      required: continueWithPassword,
                    },
                  ]}
                  label={<FormattedMessage id="new_password" />}
                >
                  <Input
                    autoComplete="new-password"
                    name={PasswordsFields.New}
                    type={passwordToggle.type}
                    placeholder={pwdPlaceholder}
                    className={` ${successVisbility ? styles.greenBorder : ''}`}
                    autoFocus={
                      !(
                        isPartnerAdmin &&
                        data?.sessionPayload.user.role?.roleId == null
                      )
                    }
                    suffix={
                      successVisbility ? (
                        <StyledCheckmarkIcon />
                      ) : (
                        <passwordToggle.icon
                          className={`${styles.eyeIcon}`}
                          onClick={() => {
                            setPasswordToggle(
                              passwordToggle.type === 'password'
                                ? { type: 'text', icon: View16 }
                                : { type: 'password', icon: ViewOff16 },
                            );
                          }}
                        />
                      )
                    }
                  />
                </FormItem>
                <FormItem
                  name={PasswordsFields.Confirm}
                  label={<FormattedMessage id="confirm_password" />}
                  dependencies={[PasswordsFields.New]}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (
                          getFieldValue('newPassword') &&
                          getFieldValue('newPassword') === value
                        ) {
                          setSuccessVisibility(true);
                          setWarningVisibility(false);

                          return Promise.resolve();
                        }

                        if (value === '') {
                          if (getFieldValue('newPassword') === '') {
                            if (warningVisibility) {
                              setWarningVisibility(false);
                            }

                            return Promise.resolve();
                          }
                        } else if (getFieldValue('newPassword') === '') {
                          if (successVisbility) {
                            setSuccessVisibility(false);
                          }
                        }

                        if (!warningVisibility) {
                          setWarningVisibility(true);
                        }
                        if (successVisbility) {
                          setSuccessVisibility(false);
                        }

                        return Promise.reject(passwordMatchText);
                      },
                    }),
                  ]}
                >
                  <Input
                    autoComplete="new-password"
                    type={passwordToggle.type}
                    placeholder={pwdPlaceholder}
                    className={` ${warningVisibility ? styles.redBorder : ''} ${
                      successVisbility ? styles.greenBorder : ''
                    }`}
                    suffix={
                      <>
                        {warningVisibility ? (
                          <StyledWarningIcon />
                        ) : successVisbility ? (
                          <StyledCheckmarkIcon />
                        ) : null}
                      </>
                    }
                  />
                </FormItem>
                <Form.Item>
                  <Button
                    htmlType="submit"
                    className={`${styles.marginTop}`}
                    loading={isSetupProfilePending}
                    type="primary"
                    icon={<ArrowRight16 />}
                    iconPlacement="right"
                  >
                    <FormattedMessage id="get_started" />
                  </Button>
                </Form.Item>
              </>
            )}

            <StyledUserInfo>
              <FormattedMessage
                id="setup_profile.user_info"
                values={{
                  email: data?.sessionPayload?.user?.emailId,
                }}
              />
              <div>
                <FormattedMessage
                  id="setup_profile.user_info_signin"
                  values={{
                    a: (chunks) => (
                      <Link to={RoutePaths.login}>
                        <LinkText>{chunks}</LinkText>
                      </Link>
                    ),
                  }}
                />
              </div>
            </StyledUserInfo>
          </Form>
        </StyledSpace>
      </Container>
    </Layout>
  );
};
